import { useState, useEffect } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Dashboard from 'services/Dashboard'
import TimeWindow from 'services/timeWindow'
import Select from 'components/Inputs/Select'
import UsedServices from './UsedServices'
import SchedulesByStatus from './SchedulesByStatus'
import MembersByMonth from './MembersByMonth'
import CancelRate from './CancelRate'
import RetentionRate from './RetentionRate'
import ReturnedMembers from './ReturnedMembers'
import MembersByGender from './MembersByGender'
import MembersByAgeRange from './MembersByAgeRange'
import BusinessesByFrequency from './BusinessesByFrequency'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'

import {
  DashboardContainer,
  LoadingContainer,
  FilterContainer,
  InfoText
} from '../styles'

export default function Sales () {
  const now = DateTime.now ().setLocale ('pt-BR')
  const [month, setMonth] = useState ({
    label: now.toFormat ('MMMM'),
    value: now.month
  })
  const [year, setYear] = useState ({
    label: now.toFormat ('yyyy'),
    value: now.year
  })
  const [loading, setLoading] = useState (true)
  const periodFilterOptions = Dashboard.PERIOD_FILTERS
  const [periodFilter, setPeriodFilter] = useState (periodFilterOptions[0])
  const [usedServices, setUsedServices] = useState (null)
  const [cancelRate, setCancelRate] = useState (null)
  const [schedulesByStatus, setSchedulesByStatus] = useState (null)
  const [membersByMonth, setMembersByMonth] = useState (null)
  const [membersByGender, setMembersByGender] = useState (null)
  const [membersByAgeRange, setMembersByAgeRange] = useState (null)
  const [retentionRate, setRetentionRate] = useState (null)
  const [returnedMembers, setReturnedMembers] = useState (null)
  const [moreFrequentBusinesses, setMoreFrequentBusinesses] = useState (null)
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [month, year, periodFilter])

  const getData = async () => {
    try {
      setLoading (true)

      const selectedDate = DateTime.local (year.value, month.value)
      const startDate = selectedDate.minus ({ months: periodFilter.value }).startOf ('month').toISODate ()
      const endDate = selectedDate.endOf ('month').toISODate ()
      const filters = `start_date=${startDate}&end_date=${endDate}`

      const [
        { data: usedServices },
        { data: cancelRate },
        { data: retentionRate },
        { data: schedulesByStatus },
        { data: membersByMonth },
        { data: membersByGender },
        { data: membersByAgeRange },
        { data: returnedMembers },
        { data: moreFrequentBusinesses }
      ] = await Promise.all ([
        cutileiApi.get (`/admin/dashboard/used_services?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/cancel_rate?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/retention_rate?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/schedules_by_status?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/members_by_month?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/members_by_gender?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/members_by_age_range?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/returned_members?${filters}`, requestConfig),
        cutileiApi.get (`/admin/dashboard/businesses_by_frequency?${filters}&order=desc`, requestConfig)
      ])

      setUsedServices (usedServices)
      setCancelRate (cancelRate)
      setRetentionRate (retentionRate)
      setSchedulesByStatus (schedulesByStatus)
      setMembersByMonth (membersByMonth)
      setMembersByGender (membersByGender)
      setMembersByAgeRange (membersByAgeRange)
      setReturnedMembers (returnedMembers)
      setMoreFrequentBusinesses (moreFrequentBusinesses)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  return (
    <>
      <FilterContainer style={{width: 540, marginTop: 0, marginBottom: 20}}>
        <Select
          options={TimeWindow.getMonths ()}
          value={month}
          onChange={(_, value) => setMonth (value)}
        />
        <Select
          options={TimeWindow.getYears ('2021', now.year.toString ())}
          value={year}
          onChange={(_, value) => setYear (value)}
        />
        <Select
          placeholder='Período...'
          value={periodFilter}
          options={periodFilterOptions}
          onChange={(_, option) => setPeriodFilter (option)}
        />
      </FilterContainer>
      <DashboardContainer>
        {loading ? (
          <LoadingContainer>
            <Loading/>
            <InfoText>Gerando gráficos...</InfoText>
          </LoadingContainer>
        ) : (
          <>
            <MembersByMonth data={membersByMonth}/>
            <CancelRate data={cancelRate}/>
            <RetentionRate data={retentionRate}/>
            <ReturnedMembers data={returnedMembers} month={month.value} year={year.value}/>
            <UsedServices data={usedServices} month={month.value} year={year.value}/>
            <SchedulesByStatus data={schedulesByStatus} month={month.value} year={year.value}/>
            <MembersByGender data={membersByGender} month={month.value} year={year.value}/>
            <MembersByAgeRange data={membersByAgeRange} month={month.value} year={year.value}/>
            <BusinessesByFrequency data={moreFrequentBusinesses} month={month.value} year={year.value}/>
          </>
        )}
      </DashboardContainer>
    </>
  )
}
